import { useEffect, useMemo, useRef, useState } from 'react'
import { Printer } from 'lucide-react'
import type { CreatedProduct } from '@/types/products'
import { useIssueLabels } from '@/hooks/useProductLabels'
import { LABEL_SIGNATURE_MEANINGS, issueItemFrom } from '@/lib/productLabels'
import { PrintLabelDialog, type PrintLabelRequest } from '@/components/labels/print/PrintLabelDialog'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import {
  PRODUCT_LABEL_STYLE,
  labelInnerHtml,
  storedSelectionsOf,
} from '@/components/procedure/ProductLabelPreviewDialog'
import { printLabelViaIframe, printLabelsSequentially } from '@/lib/printLabel'

/**
 * Label review for a Split Product batch.
 *
 * Laid out as a grid rather than one label per row: a split of four was four
 * screens of scrolling, and the operator's job here is to compare the codes
 * across aliquots, which needs them visible at once. Each label's print
 * button sits BESIDE it for the same reason — a button under each label
 * pushed the next one off-screen and read as a divider between them.
 *
 * "Print all" sends one job per label rather than a single document holding
 * all of them: see `printLabelsSequentially` for why a merged sheet is the
 * wrong output for a label printer.
 *
 * Either way the print popup picks the printer, one pre-print signature covers
 * what is about to print, and every label is recorded before any of them
 * prints (z8nrz7dt48); each is verified after printing from the form's labels
 * list.
 */
export function SplitLabelPreviewDialog({
  products,
  onClose,
}: {
  products: CreatedProduct[]
  onClose: () => void
}) {
  const { mutateAsync: issueLabels } = useIssueLabels()
  // What the open print popup is for: every label, or one.
  const [toPrint, setToPrint] = useState<CreatedProduct[] | null>(null)
  // Each product's latest label number, from this dialog. A ref too, so a
  // reprint is detected without rebuilding the popup's request.
  const [labelNumbers, setLabelNumbers] = useState<Record<string, number>>({})
  const numbersRef = useRef<Record<string, number>>({})

  useEffect(() => {
    if (products.length === 0) {
      setLabelNumbers({})
      numbersRef.current = {}
    }
  }, [products.length])

  const request = useMemo<PrintLabelRequest | null>(() => {
    if (!toPrint) return null
    const batch = toPrint
    const one = batch.length === 1
    return {
      subject: 'product',
      ids: batch.map(p => p.id),
      title: one ? `Product label · ${batch[0].din}` : `${batch.length} split product labels`,
      browser: {
        print: async () => {
          if (one) printOne(batch[0])
          else await printAll(batch)
        },
        previewHtml: batch.map(p => labelInnerHtml(p, storedSelectionsOf(p))),
        previewStyle: PRODUCT_LABEL_STYLE,
      },
      record: {
        signatureTitle: 'Pre-print verification',
        signatureDescription: one
          ? `Sign that the label for ${batch[0].productDescriptionCode || batch[0].din} is correct. It is recorded, then sent to the printer.`
          : `Sign that all ${batch.length} labels are correct. They are recorded, then sent to the printer one by one.`,
        meaning: LABEL_SIGNATURE_MEANINGS.prePrint,
        run: async (signingToken: string) => {
          const labels = await issueLabels({
            // A product already printed here is printing again: a reprint.
            items: batch.map(p =>
              issueItemFrom(p.id, storedSelectionsOf(p), numbersRef.current[p.id] ? 'reprint' : 'original'),
            ),
            signingToken,
            signatureMeaning: LABEL_SIGNATURE_MEANINGS.prePrint,
          })
          numbersRef.current = { ...numbersRef.current, ...Object.fromEntries(labels.map(l => [l.productId, l.number])) }
          setLabelNumbers(numbersRef.current)
          const byProduct = new Map(labels.map(l => [l.productId, l.id]))
          return batch.map(p => byProduct.get(p.id) ?? '')
        },
      },
    }
  }, [toPrint, issueLabels])

  return (
    <>
      <Dialog open={products.length > 0 && !toPrint} onOpenChange={o => !o && onClose()}>
        <DialogContent className='sm:max-w-5xl'>
          <DialogHeader>
            <DialogTitle>Split product labels</DialogTitle>
            <DialogDescription>
              {products.length} label{products.length === 1 ? '' : 's'}. Print them all as separate labels, or
              print any one on its own. You pick the printer and sign before they print.
            </DialogDescription>
          </DialogHeader>

          {/* The label classes are styled for the print iframe; mounting them
              here too (minus the body rule) makes the previews match print. */}
          <style>{PRODUCT_LABEL_STYLE.replace(/^body\{[^}]*\}/, '')}</style>
          <div className='grid max-h-[30rem] justify-items-start gap-3 overflow-y-auto py-1 lg:grid-cols-2'>
            {products.map(p => (
              <div key={p.id} className='flex items-center gap-3 rounded-xl border bg-muted/10 p-3'>
                {/* The label is a fixed 320px block; the button hugs it rather
                    than stretching, so the pair reads as one unit. */}
                <div className='shrink-0' dangerouslySetInnerHTML={{ __html: labelInnerHtml(p, storedSelectionsOf(p)) }} />
                <div className='flex shrink-0 flex-col items-start gap-1'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setToPrint([p])}
                    title={`Print ${p.productDescriptionCode || p.din}`}
                  >
                    <Printer className='mr-1.5 h-3.5 w-3.5' />
                    {labelNumbers[p.id] ? 'Print again' : 'Print'}
                  </Button>
                  {labelNumbers[p.id] && (
                    <span className='text-[11px] text-emerald-700'>Label #{labelNumbers[p.id]} recorded</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={onClose}>
              Close
            </Button>
            <Button onClick={() => setToPrint(products)}>
              <Printer className='mr-2 h-4 w-4' />
              Print All ({products.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PrintLabelDialog request={request} onClose={() => setToPrint(null)} />
    </>
  )
}

/**
 * One browser print job per label rather than one document holding all of
 * them: see `printLabelsSequentially` for why a merged sheet is the wrong
 * output for a label printer.
 */
async function printAll(batch: CreatedProduct[]) {
  await printLabelsSequentially(
    batch.map(p => ({
      title: `Product ${p.din} ${p.productDescriptionCode}`,
      bodyHtml: labelInnerHtml(p, storedSelectionsOf(p)),
    })),
    PRODUCT_LABEL_STYLE,
  )
}

function printOne(p: CreatedProduct) {
  printLabelViaIframe(`Product ${p.din} ${p.productDescriptionCode}`, labelInnerHtml(p, storedSelectionsOf(p)), PRODUCT_LABEL_STYLE)
}
